import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PageTitleProvider } from './context/PageTitleContext';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './components/common';
import PrivateRoute from './components/PrivateRoute';
import Sidebar from './components/Sidebar';
import TopNavbar from './components/TopNavbar';

import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Profile from './components/Profile';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import TicketManager from './components/TicketManager';
import CreateEditTicket from './components/CreateEditTicket';
import TicketDetails from './components/TicketDetails';

import CategoryManager from './components/CategoryManager';
import CreateEditCategory from './components/CreateEditCategory';
import TeamManager from './components/TeamManagerNew';
import CreateEditTeam from './components/CreateEditTeam';
import TagManager from './components/TagManagerNew';
import CreateEditTag from './components/CreateEditTag';
import StatusManager from './components/StatusManager';
import CreateEditStatus from './components/CreateEditStatus';
import UserManagement from './components/UserManagement';
import RoleManagement from './components/RoleManagement';
import CreateEditRole from './components/CreateEditRole';
import RegionManager from './components/RegionManager';
import CreateEditRegion from './components/CreateEditRegion';
import BranchManager from './components/BranchManager';
import CreateEditBranch from './components/CreateEditBranch';
import SenderEmailManager from './components/SenderEmailManager';
import CreateEditSenderEmail from './components/CreateEditSenderEmail';
import UserManager from './components/UserManager';
import CreateEditUser from './components/CreateEditUser';
import SLAManagement from './components/SLAManagement';
import WeeklyReport from './components/WeeklyReport';
import EscalationManager from './components/EscalationManager';
import CreateEditEscalation from './components/CreateEditEscalation';
import CategoryReport from './components/reports/CategoryReport';
import RegionReport from './components/reports/RegionReport';
import SlaReport from './components/reports/SlaReport';
import ItTeamReport from './components/reports/ItTeamReport';


// Department-specific screens. DeptPage picks the right one at render time.
import DeptPage from './components/DeptPage';
import BmEquipmentManager from './pages/bm/EquipmentManager';
import BmCreateEditEquipment from './pages/bm/CreateEditEquipment';
import BmEquipmentReport from './pages/bm/EquipmentReport';

import './styles/index.css';

// Portal segments the app serves. The department a user belongs to decides
// which one they get; the segment itself never grants access — the API scopes
// every query from the department in the JWT.
const PORTAL_PREFIXES = ['it', 'bm'];

// Read the portal out of the URL at boot, before any React state exists.
// This becomes the Router basename, so every absolute path in the app
// ('/tickets', '/admin/categories') is automatically served under it.
export const detectPortalPrefix = () => {
  const segment = window.location.pathname.split('/')[1];
  return PORTAL_PREFIXES.includes(segment) ? `/${segment}` : '';
};

// Keeps the URL and the account in agreement: a BioMedical user who lands on
// /it is moved to /bm, and a logged-in user sitting at the bare root is sent
// into their portal. Uses a full navigation because basename is fixed at mount.
const PortalGuard = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  React.useEffect(() => {
    if (loading || !user) return;
    const current = detectPortalPrefix();
    const target = user.url_prefix || '/it';
    if (current !== target) {
      const rest = current ? location.pathname : `${location.pathname}`;
      window.location.replace(`${target}${rest === '/' ? '/tickets' : rest}`);
    }
  }, [loading, user, location.pathname]);

  return null;
};

const AppContent = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { isCollapsed, isMobile, isMobileOpen, setIsMobileOpen } = useSidebar();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/forgot-password' || location.pathname === '/reset-password';

  return (
    <>
      {!isAuthPage && <PortalGuard />}
      {!isAuthPage && user && <Sidebar />}
      {!isAuthPage && user && <TopNavbar />}
      {/* Backdrop: tapping outside the drawer closes it */}
      {!isAuthPage && user && isMobile && isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <main className={`${isAuthPage ? 'w-full' : `flex-1 ${isMobile ? 'ml-0 p-2' : (isCollapsed ? 'ml-20 pr-0' : 'ml-64 p-2')} mt-16 h-[calc(100vh-64px)] overflow-auto  ccustom-sidebar-scrollbar`}`}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />

          <Route
            path="/"
            element={
              <PrivateRoute>
                <AnalyticsDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <AnalyticsDashboard />
              </PrivateRoute>
            }
          />


          <Route
            path="/tickets"
            element={
              <PrivateRoute>
                <TicketManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/tickets/create"
            element={
              <PrivateRoute>
                <CreateEditTicket mode="create" />
              </PrivateRoute>
            }
          />
          <Route
            path="/tickets/edit/:id"
            element={
              <PrivateRoute>
                <CreateEditTicket mode="edit" />
              </PrivateRoute>
            }
          />
          <Route
            path="/tickets/view/:id"
            element={
              <PrivateRoute>
                <TicketDetails />
              </PrivateRoute>
            }
          />

          <Route
            path="/reports"
            element={<Navigate to="/reports/category" replace />}
          />
          <Route
            path="/reports/category"
            element={
              <PrivateRoute requiredRoute="/reports">
                <CategoryReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/region"
            element={
              <PrivateRoute requiredRoute="/reports">
                <RegionReport />
              </PrivateRoute>
            }
          />
          {/* BioMedical's report set. Team Reports reuses IT's team report —
              the data is the same shape, only the department differs. */}
          <Route
            path="/reports/team"
            element={
              <PrivateRoute requiredRoute="/reports">
                <ItTeamReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/equipment"
            element={
              <PrivateRoute requiredRoute="/reports">
                <DeptPage bm={BmEquipmentReport} />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/sla"
            element={
              <PrivateRoute requiredRoute="/reports">
                <SlaReport />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/it-team"
            element={
              <PrivateRoute requiredRoute="/reports">
                <ItTeamReport />
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/categories"
            element={
              <PrivateRoute requiredRoute="/admin/categories">
                <CategoryManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/categories/create"
            element={
              <PrivateRoute requiredRoute="/admin/categories" requiredPermission="can_add">
                <CreateEditCategory />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/categories/edit/:id"
            element={
              <PrivateRoute requiredRoute="/admin/categories" requiredPermission="can_edit">
                <CreateEditCategory />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/categories/view/:id"
            element={
              <PrivateRoute requiredRoute="/admin/categories" requiredPermission="can_view">
                <CreateEditCategory />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/teams"
            element={
              <PrivateRoute requiredRoute="/admin/teams">
                <TeamManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/teams/create"
            element={
              <PrivateRoute requiredRoute="/admin/teams" requiredPermission="can_add">
                <CreateEditTeam />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/teams/edit/:id"
            element={
              <PrivateRoute requiredRoute="/admin/teams" requiredPermission="can_edit">
                <CreateEditTeam />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/teams/view/:id"
            element={
              <PrivateRoute requiredRoute="/admin/teams" requiredPermission="can_view">
                <CreateEditTeam />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/tags"
            element={
              <PrivateRoute requiredRoute="/admin/tags">
                <TagManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/tags/create"
            element={
              <PrivateRoute requiredRoute="/admin/tags" requiredPermission="can_add">
                <CreateEditTag />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/tags/edit/:id"
            element={
              <PrivateRoute requiredRoute="/admin/tags" requiredPermission="can_edit">
                <CreateEditTag />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/tags/view/:id"
            element={
              <PrivateRoute requiredRoute="/admin/tags" requiredPermission="can_view">
                <CreateEditTag />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/statuses"
            element={
              <PrivateRoute requiredRoute="/admin/statuses">
                <StatusManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/statuses/create"
            element={
              <PrivateRoute requiredRoute="/admin/statuses" requiredPermission="can_add">
                <CreateEditStatus />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/statuses/edit/:id"
            element={
              <PrivateRoute requiredRoute="/admin/statuses" requiredPermission="can_edit">
                <CreateEditStatus />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/statuses/view/:id"
            element={
              <PrivateRoute requiredRoute="/admin/statuses" requiredPermission="can_view">
                <CreateEditStatus />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/escalations"
            element={
              <PrivateRoute requiredRoute="/admin/escalations">
                <EscalationManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/escalations/create"
            element={
              <PrivateRoute requiredRoute="/admin/escalations" requiredPermission="can_add">
                <CreateEditEscalation />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/escalations/edit/:id"
            element={
              <PrivateRoute requiredRoute="/admin/escalations" requiredPermission="can_edit">
                <CreateEditEscalation />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/escalations/view/:id"
            element={
              <PrivateRoute requiredRoute="/admin/escalations" requiredPermission="can_view">
                <CreateEditEscalation />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/regions"
            element={
              <PrivateRoute requiredRoute="/admin/regions">
                <RegionManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/regions/create"
            element={
              <PrivateRoute requiredRoute="/admin/regions" requiredPermission="can_add">
                <CreateEditRegion />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/regions/edit/:id"
            element={
              <PrivateRoute requiredRoute="/admin/regions" requiredPermission="can_edit">
                <CreateEditRegion />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/regions/view/:id"
            element={
              <PrivateRoute requiredRoute="/admin/regions" requiredPermission="can_view">
                <CreateEditRegion />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/branches"
            element={
              <PrivateRoute requiredRoute="/admin/branches">
                <BranchManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/branches/create"
            element={
              <PrivateRoute requiredRoute="/admin/branches" requiredPermission="can_add">
                <CreateEditBranch />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/branches/edit/:id"
            element={
              <PrivateRoute requiredRoute="/admin/branches" requiredPermission="can_edit">
                <CreateEditBranch />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/branches/view/:id"
            element={
              <PrivateRoute requiredRoute="/admin/branches" requiredPermission="can_view">
                <CreateEditBranch />
              </PrivateRoute>
            }
          />

          <Route
            path="/admin/sender-emails"
            element={
              <PrivateRoute requiredRoute="/admin/sender-emails">
                <SenderEmailManager />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/sender-emails/create"
            element={
              <PrivateRoute requiredRoute="/admin/sender-emails" requiredPermission="can_add">
                <CreateEditSenderEmail />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/sender-emails/edit/:id"
            element={
              <PrivateRoute requiredRoute="/admin/sender-emails" requiredPermission="can_edit">
                <CreateEditSenderEmail />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/sender-emails/view/:id"
            element={
              <PrivateRoute requiredRoute="/admin/sender-emails" requiredPermission="can_view">
                <CreateEditSenderEmail />
              </PrivateRoute>
            }
          />

          {/* User Routes */}
          {/* BioMedical-only today. Permission comes from master_pages, so an
              IT user has no /equipment page and is refused here as well as
              never seeing it in the sidebar. */}
          <Route path="/admin/equipment" element={<PrivateRoute requiredRoute="/admin/equipment"><DeptPage bm={BmEquipmentManager} /></PrivateRoute>} />
          <Route path="/admin/equipment/create" element={<PrivateRoute requiredRoute="/admin/equipment" requiredPermission="can_add"><DeptPage bm={BmCreateEditEquipment} /></PrivateRoute>} />
          <Route path="/admin/equipment/edit/:id" element={<PrivateRoute requiredRoute="/admin/equipment" requiredPermission="can_edit"><DeptPage bm={BmCreateEditEquipment} /></PrivateRoute>} />
          <Route path="/admin/equipment/view/:id" element={<PrivateRoute requiredRoute="/admin/equipment" requiredPermission="can_view"><DeptPage bm={BmCreateEditEquipment} /></PrivateRoute>} />

          <Route path="/admin/users" element={<PrivateRoute requiredRoute="/admin/users"><UserManager /></PrivateRoute>} />
          <Route path="/admin/users/create" element={<PrivateRoute requiredRoute="/admin/users" requiredPermission="can_add"><CreateEditUser /></PrivateRoute>} />
          <Route path="/admin/users/edit/:id" element={<PrivateRoute requiredRoute="/admin/users" requiredPermission="can_edit"><CreateEditUser /></PrivateRoute>} />
          <Route path="/admin/users/view/:id" element={<PrivateRoute requiredRoute="/admin/users" requiredPermission="can_view"><CreateEditUser /></PrivateRoute>} />

          <Route
            path="/users"
            element={
              <PrivateRoute requiredRoute="/admin/users">
                <UserManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <PrivateRoute requiredRoute="/roles">
                <RoleManagement />
              </PrivateRoute>
            }
          />
          <Route path="/roles/create" element={<PrivateRoute requiredRoute="/roles" requiredPermission="can_add"><CreateEditRole /></PrivateRoute>} />
          <Route path="/roles/edit/:id" element={<PrivateRoute requiredRoute="/roles" requiredPermission="can_edit"><CreateEditRole /></PrivateRoute>} />
          <Route path="/roles/view/:id" element={<PrivateRoute requiredRoute="/roles" requiredPermission="can_view"><CreateEditRole /></PrivateRoute>} />
          <Route
            path="/admin/sla-rules"
            element={
              <PrivateRoute requiredRoute="/admin/sla-rules">
                <SLAManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/weekly"
            element={
              <PrivateRoute adminOnly>
                <WeeklyReport />
              </PrivateRoute>
            }
          />

          <Route path="/" element={<Navigate to="/tickets" replace />} />
          <Route path="*" element={<Navigate to="/tickets" replace />} />
        </Routes>
      </main>
    </>
  );
};

const App = () => {
  return (
    <Router basename={detectPortalPrefix()}>
      <AuthProvider>
        <PageTitleProvider>
          <ThemeProvider>
            <SidebarProvider>
              <ToastProvider>
                <AppContent />
              </ToastProvider>
            </SidebarProvider>
          </ThemeProvider>
        </PageTitleProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
